Ext.define('SA.model.Country', {
    statics: {
        get(country) {
            return SA.Institution.getCountry(country);
        },

        isCanada(country) {
            return this.get(country).isCanada();
        }
    },

    constructor(params) {
        Object.assign(this, params);
    },

    getCensusReleaseRange(censusReleaseYear) {
        var nextCensusReleaseYear = this.getNextCensusReleaseYear(censusReleaseYear);

        return [censusReleaseYear, nextCensusReleaseYear - 1];
    },

    getCensusReleasesSortedAscending() {
        return Object.keys(this.censusReleases).map(year => Number(year)).sort();
    },

    getCode() {
        return this.code;
    },

    getDatasets() {
        var countryCode = this.getCode();

        return SA.data.Vendors.getDatasetModels().filter(dataset => dataset.hasCountryCode(countryCode));
    },

    getDatasetIdsForVendor(vendorName) {
        return this.getDatasets()
            .filter(dataset => dataset.getVendorName() === vendorName)
            .map(dataset => dataset.getId());
    },

    getEditions(view) {
        return this.censusReleases[view.getCensusRelease()].editions;
    },

    getEditionRangesByCensusRelease() {
        return this.getEditionsByCensusRelease().map(({censusRelease, editions}) => {
            var currentYear = SA.System.getYear(),
                groups = [[editions.slice().shift()]],
                groupToRange = group => [Math.min(...group), Math.max(...group)];

            editions.forEach(edition => {
                var currentGroup = SA.Array.getLast(groups),
                    priorEdition = SA.Array.getLast(currentGroup),
                    isNonContiguous = edition > priorEdition + 1,
                    isFirstProjection = (edition > currentYear) && (priorEdition <= currentYear);

                if (isNonContiguous || isFirstProjection) {
                    groups.push([edition]); 
                    return;
                }

                currentGroup.push(edition);
            });

            return {censusRelease, ranges: groups.map(groupToRange)};
        });
    },

    getEditionsByCensusRelease() {
        var entries = Object.entries(this.censusReleases),
            mapEntry = ([censusRelease, {editions}]) => ({censusRelease, editions});

        return entries.map(mapEntry);
    },

    getGeographicUnit() {
        return this.geographicUnit;
    },

    getGeographicUnitsForCensusRelease(censusRelease) {
        return this.censusReleases[censusRelease].geographicUnits;
    },

    getGeoUnitsForCensusReleaseYear(censusReleaseYear) {
        var unitsData = this.getGeographicUnitsForCensusRelease(censusReleaseYear).slice().sort((a, b) => b.order - a.order),
            subUnits = [];

        return unitsData.map(({id, name: pluralName, singularName}) => {
            var unit = new SA.model.GeoUnit({id, singularName, pluralName, subUnits});

            subUnits = [unit, ...subUnits];

            return unit;
        });
    },

    getLastYearInCensusRelease(censusRelease) {
        var censusReleases = this.getCensusReleasesSortedAscending(),
            nextCensusReleaseIndex = censusReleases.indexOf(censusRelease) + 1;

        if (nextCensusReleaseIndex >= censusReleases.length) {
            return null;
        }

        return censusReleases[nextCensusReleaseIndex] - 1;
    },

    getLatestCensusRelease() {
        return this.latestRelease;
    },

    getLatestGeographicRelease() {
        return this.getLatestGeographicReleaseForCensusRelease(this.getLatestCensusRelease());
    },

    getLatestGeographicReleaseForCensusRelease(censusRelease) {
        return this.censusReleases[censusRelease].latestGeographicRelease;
    },

    getLatestHistoricalCensusRelease() {
        var releases = this.getCensusReleasesSortedAscending();

        return releases[releases.length - 2]; 
    },

    getLocationSeries() {
        return this.locationSeries;
    },

    getMetersPerRadiusUnit() {
        return this.metersPerRadiusUnit;
    },

    getName() {
        return this.name;
    },

    getNextCensusReleaseYear(censusReleaseYear) {
        var censusReleases = this.getCensusReleasesSortedAscending(),
            index = censusReleases.indexOf(censusReleaseYear) + 1;

        return censusReleases[index];
    },

    getSpatialIdColumnExportName({geographicUnit}) {
        return this.isUSA() ? 'FIPS' : `${SA.data.GeographicUnits.get(geographicUnit).singularName} ID`;
    },

    getVendors() {
        return SA.data.Vendors.forCountry(this.getCode());
    },

    hasCensusRelease(censusRelease) {
        return censusRelease in this.censusReleases;
    },

    isCanada() {
        return this.getCode() === 'CAN';
    },

    isLatestCensusRelease(censusRelease) {
        return censusRelease === this.getLatestCensusRelease();
    },

    isUSA() {
        return this.getCode() === 'USA';
    },

    maybeRemoveDMAs() {
        if (this.getDatasets().some(dataset => dataset.isAnyScarborough())) {
            return;
        }

        Object.values(this.censusReleases).forEach(release => {
            release.geographicUnits = release.geographicUnits.filter(unit => unit.geographicUnit !== 'usa:dma');
        });
    },

    supportsAcsTables() {
        return this.getDatasets().some(dataset => dataset.isACS());
    },

    supportsScarboroughCrosstabs() {
        return this.getDatasets().some(dataset => dataset.isScarboroughCrosstab());
    },

    supportsSimmonsCrosstabs() {
        return this.getDatasets().some(dataset => dataset.isSimmonsCrosstab());
    }
}, cls => {
    SA.Country = cls;
});